import React from 'react';
import styled from 'styled-components';

/**
 * Professional switch for lead state: Running | Paused.
 * Shows both labels; value is 'running' | 'paused' (API convention).
 * PATCH /api/v1/state/leads/:leadId with { state }.
 */
export function LeadStateSwitch({ value, onChange, disabled = false, loading = false, ...props }) {
    const isRunning = (value || '').toLowerCase() === 'running';

    const handleClick = () => {
        if (disabled || loading) return;
        onChange?.(isRunning ? 'paused' : 'running');
    };

    return (
        <Wrap
            role="switch"
            aria-checked={isRunning}
            aria-label={isRunning ? 'Running' : 'Paused'}
            $disabled={disabled || loading}
            onClick={handleClick}
            {...props}
        >
            <Track>
                <Segment $active={!isRunning}>Paused</Segment>
                <Segment $active={isRunning}>Running</Segment>
            </Track>
            <Knob $isRunning={isRunning}>{loading ? <Spinner /> : isRunning ? 'Running' : 'Paused'}</Knob>
        </Wrap>
    );
}

const Wrap = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
    min-width: 140px;

    &:focus-visible {
        outline: 2px solid var(--ant-color-primary);
        outline-offset: 2px;
        border-radius: 20px;
    }
`;

const Track = styled.span`
    display: flex;
    align-items: center;
    width: 100%;
    height: 32px;
    padding: 2px;
    border-radius: 20px;
    background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
    border: 1px solid
        ${({ theme }) =>
            theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
    box-sizing: border-box;
`;

const Segment = styled.span`
    flex: 1;
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: ${({ $active, theme }) =>
        $active
            ? theme?.colors?.background?.default === '#141414'
                ? 'rgba(255,255,255,0.45)'
                : 'rgba(0,0,0,0.45)'
            : 'transparent'};
    transition: color 0.2s ease;
    z-index: 0;
`;

const Knob = styled.span`
    position: absolute;
    top: 2px;
    left: 2px;
    width: 68px;
    height: 26px;
    transform: translateX(${({ $isRunning }) => ($isRunning ? '70px' : '0')});
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 18px;
    background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414'
            ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
            : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: transform 0.25s ease;
    z-index: 1;
`;

const Spinner = styled.span`
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: leadStateSpin 0.7s linear infinite;
    @keyframes leadStateSpin {
        to {
            transform: rotate(360deg);
        }
    }
`;

export default LeadStateSwitch;
